import {useState} from "react";
import {
    FormattedButton,
    useBoolState,
    FormActionButtons,
    FormattedFormRow,
    FormContainer,
    FormRowDivider,
    TextField,
} from "@dvrt/graphql-widgets";
import {Typography} from "@mui/material";

import {makeStyles} from "tss-react/mui";

import UpdateForm from "./UpdateForm";
import {CheckboxInput} from "./widgets";
import {FormattedMessage, defineMessage} from "react-intl";
import {CallForwardSelect} from "./CallForwardSelect";
import {ActivationTime, ProfileSpecialDay, SecretaryMode, UserProfile} from "@/api";
import {RuleWidget} from "./RuleWidget";
import {useHookClass, EnumField, NumberField} from "@dvrt/graphql-widgets";
import {useDataSource, useRestrictedDisabled} from "./tools";
import {ActivationTimes} from "@/components/ActivationTimes";
import {showNotification} from "@/actions";
import {useDispatch} from "react-redux";
import ProfileSpecialDayPicker from "@/components/ProfileSpecialDayPicker";
import {SECRETARY_MODE_MESSAGES} from "@/utils/translations";

type RecursivePartial<T> = {
    [P in keyof T]?: RecursivePartial<T[P]>;
};

type Props = {
    profileId: number;
    activate: () => void;
    isActive: boolean;
    otherProfiles: RecursivePartial<UserProfile>[];
};

const useStyles = makeStyles()({
    disabled: {
        pointerEvents: "none",
        opacity: 0.4,
    },
    noAnswerTimeout: {
        display: "flex",
        alignItems: "center",
    },
});

const UpdateExtensionProfileForm = ({profileId, activate, isActive, otherProfiles}: Props) => {
    const dataSource = useDataSource();
    const [enabled, enable, disable] = useBoolState(true);
    const [notUniqueActivationTime, setNotUniqueValidationTime] = useState(false);
    const [notUniqueSpecificActivationTime, setNotUniqueSpecificActivationTime] = useState(false);
    const {classes, cx} = useStyles();
    const dispatch = useDispatch();

    const form = useHookClass(UpdateForm, profileId);

    const disableCallForwards = form.values.profile.callForwardUnconditional || !enabled;

    const noAnswerTimeoutDisabled = useRestrictedDisabled("callForwardNoanswerTimeout", disableCallForwards);
    const lockingFeatureRestriction = useRestrictedDisabled("locked", !enabled);
    const callRejectionFeatureRestriction = useRestrictedDisabled("anonymousCallRejection", !enabled);
    const numberPresentationFeatureRestriction = useRestrictedDisabled(
        "callingNumberPresentationRestriction",
        !enabled,
    );

    async function submit() {
        disable();
        const response = await form.submit();

        if (!response.length) {
            dispatch(
                showNotification(defineMessage({id: "UserProfile.success", defaultMessage: "User profile updated"})),
            );
            await dataSource.fetch();
        } else {
            if (!response.find((error) => error.type.startsWith("value_error."))) {
                dispatch(
                    showNotification(
                        defineMessage({id: "unexpected_error", defaultMessage: "Unexpected error"}),
                        "error",
                    ),
                );
            }
        }
        enable();
    }

    const checkActivationTimeIsUnique = (newActivationTimes: ActivationTime[]) => {
        if (!newActivationTimes.length) {
            setNotUniqueValidationTime(false);
        }

        outer_loop: for (const group of newActivationTimes) {
            for (const wd of group.weekdays) {
                for (const other of otherProfiles) {
                    for (const otherProfileTime of other.activationTimes) {
                        if (otherProfileTime.weekday == wd) {
                            for (const t of group.times) {
                                if (otherProfileTime.activationTime == t) {
                                    setNotUniqueValidationTime(true);
                                    break outer_loop;
                                }
                            }
                        }
                    }
                }
            }
            setNotUniqueValidationTime(false);
        }
    };

    const datesMonthAndDayAreEqual = (dateOne: string, dateTwo: string): boolean => {
        return dateOne.split("-")[1] == dateTwo.split("-")[1] && dateOne.split("-")[2] == dateTwo.split("-")[2];
    };

    const checkSpecificActivationTimeIsUnique = (newActivationTimes: ProfileSpecialDay[]) => {
        if (!newActivationTimes.length) {
            setNotUniqueSpecificActivationTime(false);
        }

        outer_loop: for (const time of newActivationTimes) {
            for (const other of otherProfiles) {
                for (const otherProfileTime of other.specialDays) {
                    if (
                        time.activationTime &&
                        ((otherProfileTime.activationTime == time.activationTime && otherProfileTime.day == time.day) ||
                            ((otherProfileTime.everyYear || time.everyYear) &&
                                datesMonthAndDayAreEqual(otherProfileTime.day, time.day)))
                    ) {
                        setNotUniqueSpecificActivationTime(true);
                        break outer_loop;
                    }
                }
            }
            setNotUniqueSpecificActivationTime(false);
        }
    };

    return (
        <div style={{marginBottom: 20, marginTop: 10}}>
            <FormContainer>
                <FormattedFormRow>
                    <FormattedButton
                        message={defineMessage({
                            id: "extensionProfileActivation.button",
                            defaultMessage: "Activate profile",
                        })}
                        size="small"
                        onClick={activate}
                        disabled={isActive}
                    />
                </FormattedFormRow>
                <FormattedFormRow label={defineMessage({id: "displayName", defaultMessage: "Name"})}>
                    <TextField
                        value={form.values.profile.name}
                        onChange={(value) => form.updateProfileValues({name: value})}
                        disabled={!enabled}
                    />
                </FormattedFormRow>
                <FormRowDivider />
                <FormattedFormRow label={defineMessage({id: "stickyProfile", defaultMessage: "Sticky profile"})}>
                    <CheckboxInput form={form} field="stickyProfile" disabled={!enabled} />
                </FormattedFormRow>
                <FormattedFormRow
                    label={defineMessage({id: "activationTime.label", defaultMessage: "Activation times"})}
                >
                    <ActivationTimes
                        activationTimes={form.values.profile.settings.activationTimes}
                        disabled={!enabled}
                        updater={(settings) => form.updateActivationSettings(settings)}
                        uniqueValidator={checkActivationTimeIsUnique}
                    />
                    {notUniqueActivationTime && (
                        <Typography
                            variant="subtitle2"
                            style={{flex: 1, cursor: "default", color: "red", textAlign: "right"}}
                        >
                            <FormattedMessage
                                id="ivr.activationTimes.notUnique"
                                defaultMessage="The added activation time is already added to another profile"
                            />
                        </Typography>
                    )}
                </FormattedFormRow>
                <FormattedFormRow
                    label={defineMessage({
                        id: "specialDays.profile.label",
                        defaultMessage: "Specific activation times",
                    })}
                >
                    <ProfileSpecialDayPicker
                        specialDays={form.values.profile.settings.specialDays}
                        disabled={!enabled}
                        updater={(newActivationTimes) =>
                            form.updateActivationSettings({specialDays: newActivationTimes})
                        }
                        uniqueValidator={checkSpecificActivationTimeIsUnique}
                    />
                    {notUniqueSpecificActivationTime && (
                        <Typography
                            variant="subtitle2"
                            style={{flex: 1, cursor: "default", color: "red", textAlign: "right"}}
                        >
                            <FormattedMessage
                                id="specialDays.profile.notUnique"
                                defaultMessage="The added specific activation time is already added to another profile"
                            />
                        </Typography>
                    )}
                </FormattedFormRow>
                <FormRowDivider />
                <FormattedFormRow label={defineMessage({id: "locked", defaultMessage: "Locked"})}>
                    <CheckboxInput form={form} field="locked" disabled={!enabled || lockingFeatureRestriction} />
                </FormattedFormRow>
                <FormattedFormRow
                    label={defineMessage({
                        id: "callingNumberPresentationRestriction",
                        defaultMessage: "Calling number presentation restriction",
                    })}
                >
                    <CheckboxInput
                        form={form}
                        field="callingNumberPresentationRestriction"
                        disabled={!enabled || numberPresentationFeatureRestriction}
                    />
                </FormattedFormRow>
                <FormattedFormRow
                    label={defineMessage({
                        id: "connectedNumberPresentationRestriction",
                        defaultMessage: "Connected number presentation restriction",
                    })}
                >
                    <CheckboxInput
                        form={form}
                        field="connectedNumberPresentationRestriction"
                        disabled={!enabled || numberPresentationFeatureRestriction}
                    />
                </FormattedFormRow>
                <FormattedFormRow
                    label={defineMessage({id: "anonymousCallRejection", defaultMessage: "Anonymous call rejection"})}
                >
                    <CheckboxInput
                        form={form}
                        field="anonymousCallRejection"
                        disabled={!enabled || callRejectionFeatureRestriction}
                    />
                </FormattedFormRow>
                <FormattedFormRow
                    label={defineMessage({
                        id: "callForward.callForwardUnconditionalNumber",
                        defaultMessage: "Unconditional call forward",
                    })}
                >
                    <CallForwardSelect
                        profileId={profileId}
                        form={form}
                        field="callForwardUnconditionalNumber"
                        disabled={!enabled}
                        callPark
                        voicemail
                    />
                </FormattedFormRow>
                <FormattedFormRow
                    label={defineMessage({id: "callForward.parallelRingingNumber", defaultMessage: "Parallel ringing"})}
                >
                    <CallForwardSelect
                        profileId={profileId}
                        form={form}
                        field="parallelRingingNumber"
                        disabled={disableCallForwards}
                    />
                </FormattedFormRow>
                <FormattedFormRow
                    label={defineMessage({
                        id: "callForward.callForwardBusyNumber",
                        defaultMessage: "Call forward, if busy",
                    })}
                >
                    <CallForwardSelect
                        profileId={profileId}
                        form={form}
                        field="callForwardBusyNumber"
                        disabled={disableCallForwards}
                        callPark
                        voicemail
                    />
                </FormattedFormRow>
                <FormattedFormRow
                    label={defineMessage({
                        id: "callForward.callForwardUnavailableNumber",
                        defaultMessage: "Call forward, if unavailable",
                    })}
                >
                    <CallForwardSelect
                        profileId={profileId}
                        form={form}
                        field="callForwardUnavailableNumber"
                        disabled={disableCallForwards}
                        callPark
                        voicemail
                    />
                </FormattedFormRow>
                <FormattedFormRow
                    label={defineMessage({
                        id: "callForward.callForwardNoanswerNumber",
                        defaultMessage: "Call forward, if no answer",
                    })}
                >
                    <CallForwardSelect
                        profileId={profileId}
                        form={form}
                        field="callForwardNoanswerNumber"
                        disabled={disableCallForwards}
                        callPark
                        voicemail
                        extra={
                            <div className={cx(classes.noAnswerTimeout, {[classes.disabled]: noAnswerTimeoutDisabled})}>
                                <div style={{margin: 10}}>
                                    <FormattedMessage id="timeout" defaultMessage="Timeout" />:
                                </div>
                                <NumberField
                                    value={form.values.profile.callForwardNoanswerTimeout}
                                    update={(value) => form.updateProfileValues({callForwardNoanswerTimeout: value})}
                                    min={1}
                                    max={90}
                                    disabled={noAnswerTimeoutDisabled}
                                />
                            </div>
                        }
                    />
                </FormattedFormRow>
                <FormattedFormRow
                    label={defineMessage({
                        id: "callForward.callForwardFaultNumber",
                        defaultMessage: "Call forward on failure",
                    })}
                >
                    <CallForwardSelect
                        profileId={profileId}
                        form={form}
                        field="callForwardFaultNumber"
                        disabled={!enabled}
                        callPark
                        voicemail
                    />
                </FormattedFormRow>
                <FormattedFormRow label={defineMessage({id: "doNotDisturb", defaultMessage: "Do not disturb"})}>
                    <CheckboxInput
                        form={form}
                        field="doNotDisturb"
                        disabled={!enabled || callRejectionFeatureRestriction}
                    />
                </FormattedFormRow>
                <FormattedFormRow
                    label={defineMessage({id: "rejectDivertedCalls", defaultMessage: "Reject diverted calls"})}
                >
                    <CheckboxInput
                        form={form}
                        field="rejectDivertedCalls"
                        disabled={!enabled || callRejectionFeatureRestriction}
                    />
                </FormattedFormRow>
                {dataSource.data.current.extension.secretaryNumberId && (
                    <FormattedFormRow
                        label={defineMessage({id: "SecretaryMode.label", defaultMessage: "Secretary mode"})}
                    >
                        <EnumField
                            disabled={!enabled}
                            value={form.values.profile.secretaryMode}
                            enum_={{SecretaryMode}}
                            onChange={(val: string) => form.updateProfileValues({secretaryMode: val as SecretaryMode})}
                            translations={SECRETARY_MODE_MESSAGES}
                        />
                    </FormattedFormRow>
                )}
                <FormRowDivider />
                <FormattedFormRow
                    label={defineMessage({id: "extensionProfileRule.title", defaultMessage: "Selective call handling"})}
                >
                    <RuleWidget enabled={enabled} form={form} />
                </FormattedFormRow>
                <FormattedFormRow>
                    <div style={{margin: "1em 0"}}>
                        <FormActionButtons
                            mainAction={submit}
                            secondaryAction={() => {
                                form.reset();
                            }}
                            enabled={
                                form.hasChanged &&
                                enabled &&
                                !notUniqueActivationTime &&
                                !notUniqueSpecificActivationTime
                            }
                        />
                    </div>
                </FormattedFormRow>
            </FormContainer>
        </div>
    );
};

export default UpdateExtensionProfileForm;
